import React, { useEffect, useMemo, useState } from "react";
import api, { money, STRIPE_TEST_MODE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import AiSearchExplainer from "@/components/AiSearchExplainer";
import { TermsAgree, TERMS_VERSION } from "@/components/TermsAgree";
import { Check, Loader2, Mail, Sparkles, Bot, Search, Star, MapPin, ShieldCheck, ListOrdered, Facebook, Globe, Flame } from "lucide-react";

const AD_SIZES = {
  premium: { price: 1800, size: "4×9 in", label: "Premium Hero", blurb: "The single 4×9 hero spot in the top-left corner of the front — the biggest, first thing every household sees." },
  standard: { price: 1300, size: "4×6 in", label: "Standard", blurb: "One of two 4×6 spots (front-bottom & back-bottom). Big, bold, and impossible to miss." },
  basic: { price: 900, size: "4×3 in", label: "Basic", blurb: "A sharp 4×3 spot on the shared mailer — the most affordable way to hit 10,000 homes." },
};
const TERMS = [
  { key: "per_drop", drops: 1, label: "Single drop", note: "Try one drop" },
  { key: "4mo", drops: 4, label: "4-month", note: "Minimum for momentum" },
  { key: "8mo", drops: 8, label: "8-month", note: "Great value" },
  { key: "12mo", drops: 12, label: "12-month", note: "Best results", best: true },
];

const AI_FEATURES = [
  { icon: Bot, title: "AI Directory Assistant", text: "Shoppers ask our AI for a trusted local pro — and it recommends your business by name." },
  { icon: Search, title: "AI-Powered Search", text: "Smart search + nearby-city fallback surfaces your listing even when people search the town next door." },
  { icon: Star, title: "AI Deal & Review Copy", text: "One click drafts a punchy postcard offer and summarizes your reviews to win the click." },
  { icon: MapPin, title: "City & Metro SEO Pages", text: "Your listing lives on optimized city/metro pages that rank and get shared." },
];

export default function Mailer() {
  const { user, openAuth } = useAuth();
  const [sp] = useSearchParams();
  const [myBiz, setMyBiz] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [urgency, setUrgency] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [size, setSize] = useState("premium");
  const [onlineTos, setOnlineTos] = useState(false);
  const [form, setForm] = useState({
    business_id: "", term: "12mo", central_zip: "", city: "", state_abbr: "",
    contact_name: "", contact_email: "", contact_phone: "", deal_headline: "", deal_details: "", tos: false,
  });

  useEffect(() => {
    if (user) api.get("/my/businesses").then(({ data }) => setMyBiz(data.filter((b) => b.listing_status === "approved"))).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!open || (form.central_zip || "").length < 5) { setCoverage(null); return; }
    const t = setTimeout(() => {
      api.get(`/mailer/coverage?zip=${encodeURIComponent(form.central_zip)}`).then(({ data }) => setCoverage(data)).catch(() => setCoverage(null));
    }, 400);
    return () => clearTimeout(t);
  }, [open, form.central_zip]);

  useEffect(() => {
    const z = sp.get("zip");
    if (z) setForm((f) => ({ ...f, central_zip: z, city: sp.get("city") || f.city, state_abbr: (sp.get("state") || f.state_abbr).toUpperCase() }));
  }, [sp]);

  useEffect(() => {
    const z = sp.get("zip");
    if (!z) return;
    api.get(`/mailer/coverage?zip=${encodeURIComponent(z)}`).then(({ data }) => {
      const remaining = Math.max(0, (data.total_cap || 0) - (data.total_booked || 0));
      setUrgency({ zip: z, remaining, urgent: sp.get("urgent") === "1" });
    }).catch(() => {});
    const until = sp.get("until");
    if (until) {
      const tick = () => {
        const ms = new Date(until).getTime() - Date.now();
        if (ms <= 0) { setCountdown("offer expired"); return; }
        const h = Math.floor(ms / 3.6e6), m = Math.floor((ms % 3.6e6) / 6e4);
        setCountdown(`${h}h ${m}m left`);
      };
      tick(); const t = setInterval(tick, 60000); return () => clearInterval(t);
    }
  }, [sp]);

  const term = useMemo(() => TERMS.find((t) => t.key === form.term) || TERMS[0], [form.term]);
  const total = AD_SIZES[size].price * term.drops;
  const sizeFull = coverage && coverage.spaces?.[size] && coverage.spaces[size].remaining <= 0;

  const startOrder = (sz) => {
    if (!user) return openAuth("owner");
    setSize(sz);
    const b = myBiz[0];
    setForm((f) => ({
      ...f,
      business_id: b?.id || "",
      city: f.city || b?.city || "",
      state_abbr: f.state_abbr || b?.state_abbr || "",
      central_zip: f.central_zip || b?.zip || "",
      contact_name: user?.name || f.contact_name,
      contact_email: user?.email || f.contact_email,
      contact_phone: b?.phone || f.contact_phone,
    }));
    setOpen(true);
  };

  const submit = async () => {
    if (!form.business_id) return toast.error("Choose a business");
    if (!form.central_zip || !form.city || !form.state_abbr) return toast.error("Central ZIP, city and state are required");
    if (sizeFull) return toast.error(`${AD_SIZES[size].label} space is fully booked for ZIP ${form.central_zip}`);
    if (!form.tos) return toast.error("Please accept the Terms of Service to continue");
    setBusy(true);
    try {
      const payload = {
        business_id: form.business_id, ad_size: size, term: form.term,
        central_zip: form.central_zip, city: form.city, state_abbr: form.state_abbr,
        contact_name: form.contact_name, contact_email: form.contact_email, contact_phone: form.contact_phone,
        deal_headline: form.deal_headline, deal_details: form.deal_details,
      };
      // 1) capture the lead in the CRM the moment the form is submitted
      let lead_id = null;
      try { const { data } = await api.post("/mailer-lead", payload); lead_id = data.lead_id; } catch { /* non-blocking */ }
      // 2) start checkout
      const { data } = await api.post("/payments/mailer", { ...payload, lead_id, origin_url: window.location.origin, affiliate_id: window.affiliateId || undefined, terms_accepted: true, terms_version: TERMS_VERSION });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const upgradeOnline = async () => {
    if (!user) return openAuth("owner");
    if (myBiz.length === 0) return toast.error("Add an approved business first from your dashboard.");
    if (!onlineTos) return toast.error("Please accept the Terms of Service to continue");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/online-upgrade", { business_id: myBiz[0].id, origin_url: window.location.origin, affiliate_id: window.affiliateId || undefined, terms_accepted: true, terms_version: TERMS_VERSION });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10" data-testid="mailer-page">
      {urgency && (urgency.urgent || urgency.remaining <= 3) && (
        <div className="mb-6 rounded-2xl border-2 border-sam-crimson bg-sam-crimson/5 p-4 flex flex-wrap items-center gap-3" data-testid="urgency-banner">
          <Flame className="h-6 w-6 text-sam-crimson shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-display font-black text-sam-navy">A spot just opened in ZIP {urgency.zip}!</p>
            <p className="text-sm text-sam-slate">
              {urgency.remaining <= 0 ? "This card is full — join the waitlist." : <>Only <b className="text-sam-crimson">{urgency.remaining}</b> space{urgency.remaining === 1 ? "" : "s"} left on this card.</>}
              {countdown && <span className="font-semibold text-sam-crimson"> · ⏳ {countdown} to claim</span>}
            </p>
          </div>
          <Button className="bg-sam-crimson hover:bg-sam-crimson/90" onClick={() => setOpen(true)} data-testid="urgency-claim-btn">Claim this spot</Button>
        </div>
      )}
      {/* Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-sam-navy to-sam-navy2 text-sam-cream p-8 sm:p-12 mb-10">
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-amber"><Mail className="h-4 w-4" />Plans & Pricing</span>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-black">Get found online. Get mailed to 10,000 homes.</h1>
        <p className="mt-4 text-sam-cream/75 max-w-2xl text-base">Start with a $29/mo online listing, or advertise on the Premium Postcard Mailer — every ad space <span className="text-sam-amber font-semibold">includes your online listing free</span> while your campaign runs.</p>

        <div className="mt-7" data-testid="discovery-channels">
          <div className="text-xs font-bold uppercase tracking-widest text-sam-cream/50">Get found everywhere shoppers look — beyond standard search</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-sam-amber text-sam-navy font-bold text-sm px-3.5 py-1.5 shadow-lg shadow-sam-amber/20" data-testid="channel-ai">
              <Bot className="h-4 w-4" />AI Assistants — ChatGPT, Gemini &amp; Perplexity
            </span>
            {[
              { Icon: Search, label: "Google Search" },
              { Icon: ListOrdered, label: "Google 3-Pack" },
              { Icon: MapPin, label: "Google Maps" },
              { Icon: Facebook, label: "Facebook" },
              { Icon: Globe, label: "Bing & more" },
            ].map((c) => (
              <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-sam-cream font-semibold text-sm px-3.5 py-1.5 ring-1 ring-white/15">
                <c.Icon className="h-4 w-4 text-sam-amber" />{c.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Online listing plan */}
      <div className="grid lg:grid-cols-[340px_1fr] gap-6 mb-14">
        <div className="rounded-2xl border-2 border-sam-navy/15 bg-card p-7 flex flex-col" data-testid="plan-online">
          <div className="text-xs font-bold uppercase tracking-widest text-sam-slate">Online Listing</div>
          <div className="mt-2 flex items-end gap-1"><span className="font-display text-5xl font-black text-sam-navy">$29</span><span className="text-sam-slate mb-1.5">/month</span></div>
          <p className="mt-3 text-sm text-sam-slate">The minimum to add your business to Saving America Money and be found by local shoppers.</p>
          <ul className="mt-5 space-y-2.5 text-sm text-sam-navy flex-1">
            {["Full business profile with photos, hours & links", "Products, services & unlimited deals", "Ratings, reviews & review replies", "Appears in AI assistant + smart search", "City & metro SEO pages"].map((f) => (
              <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-sam-green shrink-0 mt-0.5" />{f}</li>
            ))}
          </ul>
          <Button className="mt-6 w-full bg-sam-navy hover:bg-sam-navy2" onClick={upgradeOnline} disabled={busy || (!!user && myBiz.length > 0 && !onlineTos)} data-testid="plan-online-cta">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Start $29/mo listing
          </Button>
          {user && myBiz.length > 0 && <div className="mt-3"><TermsAgree checked={onlineTos} onChange={setOnlineTos} testid="plan-online-terms" /></div>}
        </div>

        {/* AI value props */}
        <div className="rounded-2xl border border-border bg-sam-cream/40 p-7">
          <h2 className="font-display text-lg font-bold text-sam-navy flex items-center gap-2"><Sparkles className="h-5 w-5 text-sam-crimson" />Why the online listing sells itself</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-5">
            {AI_FEATURES.map((f) => (
              <div key={f.title} className="flex gap-3">
                <div className="h-9 w-9 rounded-lg bg-sam-navy/10 grid place-items-center shrink-0"><f.icon className="h-4.5 w-4.5 text-sam-navy" /></div>
                <div><div className="font-semibold text-sam-navy text-sm">{f.title}</div><p className="text-xs text-sam-slate mt-0.5">{f.text}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Postcard mailer ad spaces */}
      <AiSearchExplainer />

      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-crimson"><Mail className="h-4 w-4" />Premium Postcard Mailer</span>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl font-black text-sam-navy">Three ad spaces. 10,000 homes per drop.</h2>
        <p className="mt-3 text-sam-slate max-w-2xl mx-auto">A 9×12 landscape mailer shared by local companies. Pick your size — <span className="font-semibold text-sam-navy">your online listing is included free</span> for the length of your campaign.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6" data-testid="mailer-tiers">
        {Object.entries(AD_SIZES).map(([key, s]) => (
          <div key={key} className={`rounded-2xl border-2 bg-card p-7 flex flex-col ${key === "premium" ? "border-sam-crimson shadow-lg" : "border-sam-navy/15"}`} data-testid={`tier-${key}`}>
            {key === "premium" && <div className="self-start -mt-10 mb-3 rounded-full bg-sam-crimson text-white text-[11px] font-bold uppercase tracking-wide px-3 py-1">Most impact</div>}
            <div className="text-xs font-bold uppercase tracking-widest text-sam-slate">{s.label} · {s.size}</div>
            <div className="mt-2 flex items-end gap-1"><span className="font-display text-4xl font-black text-sam-navy">{money(s.price)}</span><span className="text-sam-slate mb-1.5">/drop</span></div>
            <p className="mt-3 text-sm text-sam-slate flex-1">{s.blurb}</p>
            <div className="mt-3 text-xs text-sam-slate flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-sam-green" />Online listing included free</div>
            <Button className={`mt-6 w-full ${key === "premium" ? "bg-sam-crimson hover:bg-sam-crimson/90" : "bg-sam-navy hover:bg-sam-navy2"}`} onClick={() => startOrder(key)} data-testid={`tier-${key}-cta`}>
              Advertise for {money(s.price)}/drop
            </Button>
          </div>
        ))}
      </div>

      {/* Commitment disclaimer */}
      <div className="mt-8 rounded-xl border border-sam-amber/40 bg-sam-amber/10 p-5 text-sm text-sam-navy" data-testid="commitment-disclaimer">
        <span className="font-bold">Drops are monthly.</span> For best results we recommend a <span className="font-bold">12-month commitment</span> — repetition is what makes direct mail convert. We also offer <span className="font-semibold">8-month</span> and <span className="font-semibold">4-month</span> minimum plans, or a single per-drop trial. When your commitment ends and you don't renew, your online listing simply continues at $29/mo so you stay searchable.
      </div>

      {/* Order dialog */}
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="mailer-order-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{AD_SIZES[size].label} ad space — {AD_SIZES[size].size}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {myBiz.length === 0 ? (
              <p className="text-sm text-sam-slate">You need an approved business first. Add one from your <a href="/dashboard" className="text-sam-crimson font-semibold underline">dashboard</a>.</p>
            ) : (
              <>
                <div>
                  <Label>Ad space size</Label>
                  <Select value={size} onValueChange={setSize}>
                    <SelectTrigger data-testid="order-size-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(AD_SIZES).map(([k, s]) => <SelectItem key={k} value={k}>{s.label} · {s.size} · {money(s.price)}/drop</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Commitment</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="order-term-grid">
                    {TERMS.map((t) => (
                      <button key={t.key} type="button" onClick={() => setForm((f) => ({ ...f, term: t.key }))} data-testid={`order-term-${t.key}`}
                        className={`text-left rounded-lg border-2 px-3 py-2 transition-colors ${form.term === t.key ? "border-sam-crimson bg-sam-crimson/5" : "border-border hover:border-sam-crimson/40"}`}>
                        <div className="flex items-center gap-1.5 font-semibold text-sm text-sam-navy">{t.label}{t.best && <span className="rounded-full bg-sam-green/15 text-sam-green text-[9px] font-bold uppercase px-1.5 py-0.5">Best results</span>}</div>
                        <div className="text-[11px] text-sam-slate">{t.drops} drop{t.drops > 1 ? "s" : ""} · {t.note}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Business</Label>
                  <Select value={form.business_id} onValueChange={(v) => setForm((f) => ({ ...f, business_id: v }))}>
                    <SelectTrigger data-testid="order-business-select"><SelectValue placeholder="Choose your business" /></SelectTrigger>
                    <SelectContent>{myBiz.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Central ZIP</Label><Input value={form.central_zip} onChange={(e) => setForm({ ...form, central_zip: e.target.value })} placeholder="65708" data-testid="order-zip" /></div>
                  <div><Label>State</Label><Input value={form.state_abbr} onChange={(e) => setForm({ ...form, state_abbr: e.target.value.toUpperCase().slice(0, 2) })} placeholder="MO" data-testid="order-state" /></div>
                </div>
                <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Monett" data-testid="order-city" /></div>
                <p className="text-xs text-sam-slate -mt-1">We mail 10,000 homes centered on this ZIP for each drop.</p>

                {coverage && (
                  <div className="rounded-lg border border-border bg-white p-3" data-testid="coverage-panel">
                    <div className="flex items-center justify-between text-sm"><span className="font-semibold text-sam-navy flex items-center gap-1.5">ZIP {coverage.zip} availability {coverage.total_booked / coverage.total_cap >= 0.67 && coverage.total_booked < coverage.total_cap && <span className="inline-flex items-center gap-1 rounded-full bg-sam-crimson text-white text-[10px] font-bold px-2 py-0.5" data-testid="plans-filling-fast"><Flame className="h-3 w-3" />Filling fast</span>}</span><span className="text-sam-slate">{coverage.total_booked} of {coverage.total_cap} spaces booked</span></div>
                    {coverage.households != null && <div className="mt-1 text-xs text-sam-slate" data-testid="coverage-households">{coverage.households.toLocaleString()} households on this card's USPS routes</div>}
                    <div className="mt-2 grid grid-cols-3 gap-2 text-center text-[11px]">
                      {["premium", "standard", "basic"].map((k) => {
                        const s = coverage.spaces[k]; const full = s.remaining <= 0;
                        return (
                          <div key={k} className={`rounded-md p-1.5 ${full ? "bg-slate-100 text-slate-400" : "bg-sam-green/10 text-sam-navy"}`} data-testid={`coverage-${k}`}>
                            <div className="font-semibold">{AD_SIZES[k].label}</div>
                            <div>{full ? "Full" : `${s.remaining} of ${s.cap} left`}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div><Label>Deal headline (optional)</Label><Input value={form.deal_headline} onChange={(e) => setForm({ ...form, deal_headline: e.target.value })} placeholder="e.g. $50 off any repair" data-testid="order-headline" /></div>
                <div><Label>Deal details (optional)</Label><Textarea value={form.deal_details} onChange={(e) => setForm({ ...form, deal_details: e.target.value })} placeholder="Fine print, e.g. Mention Saving America Money." data-testid="order-details" /></div>

                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Contact email</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="order-email" /></div>
                  <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="order-phone" /></div>
                </div>

                <div className="rounded-lg bg-sam-cream/60 border border-border p-3 flex items-center justify-between">
                  <div className="text-sm text-sam-slate">{money(AD_SIZES[size].price)} × {term.drops} drop{term.drops > 1 ? "s" : ""} <span className="block text-[11px]">+ online listing included free</span></div>
                  <div className="font-display text-2xl font-black text-sam-navy" data-testid="order-total">{money(total)}</div>
                </div>

                <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" onClick={submit} disabled={busy || sizeFull || !form.tos} data-testid="order-pay-btn">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{sizeFull ? `${AD_SIZES[size].label} space full for this ZIP` : `Pay ${money(total)} & book campaign`}
                </Button>
                <TermsAgree checked={form.tos} onChange={(v) => setForm({ ...form, tos: v })} testid="order-terms" />
                {STRIPE_TEST_MODE && <p className="text-xs text-center text-muted-foreground">Test card 4242 4242 4242 4242 · any future date · any CVC</p>}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
